import * as React from "react";
import { Typography, TooltipProps } from "@stemscopes/ui";
import classNames from "classnames";
import RCTooltip from "rc-tooltip";
import generateId from "./generateId";
import styles from "./Tooltip.module.css";

const OFFSET = 20;

const Tooltip = ({
  id,
  position = "top",
  trigger = ["hover", "focus"],
  content,
  className = "",
  children,
  ...rest
}: TooltipProps): React.ReactElement<any> => {
  const tooltipId = React.useMemo(() => id || generateId(), [id]);
  const initiatorId = React.useMemo(() => generateId(), []);
  const [visible, setVisible] = React.useState(false);

  const placementRef = React.useRef(position);
  const [, setRerenderCount] = React.useState(0);

  // `rc-tooltip` does not rotate content correctly by default
  // if not enough space avaliable.
  React.useLayoutEffect(() => {
    const tooltipContent = document.querySelector(`[id="${initiatorId}"]`) || {
      parentElement: null,
    };
    const tooltip = tooltipContent.parentElement;
    if (!tooltip) {
      return;
    }

    const tooltipRect = tooltip.getBoundingClientRect();
    const bodyRect = document.body.getBoundingClientRect();

    if (placementRef.current === "right") {
      if (tooltipRect.left < OFFSET) {
        placementRef.current = "top";
        setRerenderCount((count) => count + 1);
      }
    }

    if (placementRef.current === "left") {
      if (bodyRect.right - tooltipRect.right < OFFSET) {
        placementRef.current = "top";
        setRerenderCount((count) => count + 1);
      }
    }

    return () => {
      placementRef.current = position;
    };
  }, [visible]);

  React.useLayoutEffect(() => {
    placementRef.current = position;
    setRerenderCount((count) => count + 1);
  }, [position]);

  const handleMutation = (mutations: MutationRecord[]): void => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "data-focus-visible"
      ) {
        if (
          (mutation.target as HTMLElement).getAttribute(
            "data-focus-visible",
          ) === null
        ) {
          setVisible(false);
        } else {
          setVisible(true);
        }
      }
    });
  };

  const handleChildrenFocus = (): (() => void) => {
    const observer = new MutationObserver(handleMutation);
    const initiator = document.getElementById(initiatorId);
    if (initiator) {
      observer.observe(initiator, { attributes: true });
    }

    return () => {
      observer.disconnect();
    };
  };

  const handleKeyboard = (): void => {
    const initiator = document.getElementById(initiatorId);

    if (visible) {
      initiator?.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setVisible(false);
          }
        },
        { once: true },
      );
    }
  };

  React.useEffect(handleChildrenFocus, []);

  React.useEffect(handleKeyboard, [visible]);

  return (
    // Wrong types. `forceRender` exists.
    // eslint-disable-next-line
    // @ts-ignore
    <RCTooltip
      {...rest}
      // @ts-ignore
      forceRender
      // `rc-tooltip@6` may anchor to wrapper when child is absolutely positioned.
      // Point trigger geometry to the real child DOM node.
      // @ts-ignore
      getTriggerDOMNode={() => {
        const triggerElement = document.getElementById(initiatorId);
        const childNode = triggerElement?.firstElementChild;
        return (childNode as HTMLElement) || triggerElement || document.body;
      }}
      destroyTooltipOnHide={false}
      visible={visible}
      onVisibleChange={setVisible}
      getTooltipContainer={() => document.body}
      id={tooltipId}
      placement={placementRef.current}
      trigger={trigger}
      overlay={<Typography variant="p2">{content}</Typography>}
      classNames={{
        root: classNames(styles.wrapper, className),
      }}
    >
      <span id={initiatorId} aria-describedby={tooltipId}>
        {React.Children.only(children)}
      </span>
    </RCTooltip>
  );
};

export default Tooltip;
